import { useState, useEffect } from 'react';
import { betLimitsApi } from '../../api/betLimits';
import { gamesApi } from '../../api/games';
import { useAuth } from '../../context/AuthContext';
import {
  Badge,
  Modal,
  Button,
  Label,
  TextInput,
  Select,
  Checkbox,
} from 'flowbite-react';
import {
  ChevronLeft,
  ChevronRight,
  Search,
  Filter,
  Sliders,
  Plus,
  Edit,
  Trash2,
  X,
  Check,
} from 'lucide-react';
import HeaderWithIcon from '../../components/common/HeaderWithIcon';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';

export default function BetLimits() {
  const [betLimits, setBetLimits] = useState([]);
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterGame, setFilterGame] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);

  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [currentLimit, setCurrentLimit] = useState(null);

  // Form states
  const [formData, setFormData] = useState({
    gameId: 1,
    combination: '',
    limit: '',
    isSoldOut: false,
    type: 'target',
  });

  const { tokens } = useAuth();
  const navigate = useNavigate();
  const itemsPerPage = 20;

  useEffect(() => {
    fetchBetLimits();
    fetchGames();
  }, [page]);

  const fetchBetLimits = async () => {
    try {
      setLoading(true);
      const data = await betLimitsApi.getBetLimits(
        tokens.access.token,
        page,
        itemsPerPage
      );
      setBetLimits(data.rows);
      setTotalPages(Math.ceil(data.count / itemsPerPage));
      setLoading(false);
    } catch (err) {
      setError('Failed to fetch bet limits. Please try again.');
      setLoading(false);
      console.error(err);
    }
  };

  const fetchGames = async () => {
    try {
      const { data } = await gamesApi.getAllGames(tokens.access.token);
      setGames(data);

      // Set default gameId to first game in the list if available
      if (data && data.length > 0) {
        setFormData((prev) => ({
          ...prev,
          gameId: data[0].id,
        }));
      }
    } catch (err) {
      console.error('Failed to fetch games:', err);
    }
  };

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]:
        type === 'checkbox'
          ? checked
          : type === 'number'
          ? Number(value)
          : value,
    });
  };

  const getGameNameById = (gameId) => {
    const game = games.find((g) => g.id === gameId);
    return game ? game.name : `Game ${gameId}`;
  };

  const handleAddLimit = async () => {
    try {
      setLoading(true);
      await betLimitsApi.createBetLimit(tokens.access.token, formData);
      setShowAddModal(false);
      // Reset form data
      setFormData({
        gameId: games.length > 0 ? games[0].id : 1,
        combination: '',
        limit: 100,
        isSoldOut: false,
        type: 'target',
      });
      // Refresh the list
      fetchBetLimits();
      Swal.fire({
        title: 'Success!',
        text: 'Bet limit added successfully',
        icon: 'success',
        confirmButtonColor: '#3085d6',
      });
    } catch (err) {
      Swal.fire({
        title: 'Error!',
        text: err.message || 'Failed to add bet limit',
        icon: 'error',
        confirmButtonColor: '#3085d6',
      });
      setLoading(false);
    }
  };

  const handleEditLimit = (limit) => {
    setCurrentLimit(limit);
    setFormData({
      gameId: limit.gameId,
      combination: limit.combination,
      limit: limit.limit,
      isSoldOut: limit.isSoldOut,
      type: limit.type,
    });
    setShowEditModal(true);
  };

  const handleUpdateLimit = async () => {
    try {
      setLoading(true);
      await betLimitsApi.updateBetLimit(
        tokens.access.token,
        currentLimit.id,
        formData
      );
      setShowEditModal(false);
      // Refresh the list
      fetchBetLimits();
      Swal.fire({
        title: 'Success!',
        text: 'Bet limit updated successfully',
        icon: 'success',
        confirmButtonColor: '#3085d6',
      });
    } catch (err) {
      Swal.fire({
        title: 'Error!',
        text: err.message || 'Failed to update bet limit',
        icon: 'error',
        confirmButtonColor: '#3085d6',
      });
      setLoading(false);
    }
  };

  const handleDeleteLimit = (id) => {
    Swal.fire({
      title: 'Are you sure?',
      text: 'You will not be able to recover this bet limit!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          setLoading(true);
          await betLimitsApi.deleteBetLimit(tokens.access.token, id);
          // Refresh the list
          fetchBetLimits();
          Swal.fire({
            title: 'Deleted!',
            text: 'The bet limit has been deleted.',
            icon: 'success',
            confirmButtonColor: '#3085d6',
          });
        } catch (err) {
          Swal.fire({
            title: 'Error!',
            text: err.message || 'Failed to delete bet limit',
            icon: 'error',
            confirmButtonColor: '#3085d6',
          });
          setLoading(false);
        }
      }
    });
  };

  const filteredBetLimits = betLimits.filter((limit) => {
    const matchesSearch =
      searchQuery === '' ||
      limit.combination.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesType = filterType === '' || limit.type === filterType;

    const matchesGame = filterGame === '' || limit.gameId == filterGame;

    return matchesSearch && matchesType && matchesGame;
  });

  const handlePrevPage = () => {
    if (page > 1) {
      setPage(page - 1);
    }
  };

  const handleNextPage = () => {
    if (page < totalPages) {
      setPage(page + 1);
    }
  };

  const renderTypeFilter = () => (
    <div
      className={`absolute top-full right-0 mt-2 bg-white rounded-lg shadow-md z-20 p-4 border border-gray-200 ${
        filterOpen ? 'block' : 'hidden'
      }`}
    >
      <h3 className='text-sm font-medium mb-2'>Filter by Type</h3>
      <div className='space-y-2'>
        <div className='flex items-center'>
          <input
            type='radio'
            id='all'
            name='filterType'
            value=''
            checked={filterType === ''}
            onChange={() => setFilterType('')}
            className='mr-2'
          />
          <label htmlFor='all' className='text-sm'>
            All
          </label>
        </div>
        <div className='flex items-center'>
          <input
            type='radio'
            id='target'
            name='filterType'
            value='target'
            checked={filterType === 'target'}
            onChange={() => setFilterType('target')}
            className='mr-2'
          />
          <label htmlFor='target' className='text-sm'>
            Target
          </label>
        </div>
        <div className='flex items-center'>
          <input
            type='radio'
            id='ramble'
            name='filterType'
            value='ramble'
            checked={filterType === 'ramble'}
            onChange={() => setFilterType('ramble')}
            className='mr-2'
          />
          <label htmlFor='ramble' className='text-sm'>
            Ramble
          </label>
        </div>
      </div>
    </div>
  );

  const renderLimitForm = () => (
    <div className='space-y-4'>
      <div>
        <div className='mb-2 block'>
          <Label htmlFor='gameId' value='Game' />
        </div>
        <Select
          id='gameId'
          name='gameId'
          value={formData.gameId}
          onChange={handleInputChange}
          required
        >
          {games.map((game) => (
            <option key={game.id} value={game.id}>
              {game.name}
            </option>
          ))}
        </Select>
      </div>
      <div>
        <div className='mb-2 block'>
          <Label htmlFor='combination' value='Combination' />
        </div>
        <TextInput
          id='combination'
          name='combination'
          value={formData.combination}
          onChange={handleInputChange}
          placeholder='Enter combination'
          required
        />
      </div>
      <div>
        <div className='mb-2 block'>
          <Label htmlFor='limit' value='Limit' />
        </div>
        <TextInput
          id='limit'
          name='limit'
          type='number'
          min='1'
          value={formData.limit || ''}
          onChange={handleInputChange}
          required
        />
      </div>
      <div>
        <div className='mb-2 block'>
          <Label htmlFor='type' value='Type' />
        </div>
        <Select
          id='type'
          name='type'
          value={formData.type}
          onChange={handleInputChange}
          required
        >
          <option value='target'>Target</option>
          <option value='ramble'>Ramble</option>
        </Select>
      </div>
      <div className='flex items-center gap-2'>
        <Checkbox
          id='isSoldOut'
          name='isSoldOut'
          checked={formData.isSoldOut}
          onChange={handleInputChange}
        />
        <Label htmlFor='isSoldOut'>Sold Out</Label>
      </div>
    </div>
  );

  return (
    <div className='bg-[#F8F9FB] min-h-screen'>
      <HeaderWithIcon title='Bet Limits' onBack={() => navigate(-1)} />

      <div className='px-4 py-3'>
        {/* Search and Filter Header */}
        <div className='mb-4 flex items-center relative'>
          <div className='relative flex-1'>
            <div className='absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none'>
              <Search size={18} className='text-gray-400' />
            </div>
            <input
              type='text'
              className='bg-white border border-gray-300 text-gray-900 text-sm rounded-lg block w-full pl-10 p-2.5'
              placeholder='Search combinations...'
              value={searchQuery}
              onChange={handleSearch}
            />
          </div>
          <div>
            <Select
              className='w-40 pl-3'
              id='filterGame'
              name='filterGame'
              value={filterGame}
              onChange={(e) => setFilterGame(e.target.value)}
              required
            >
              <option value=''>All</option>
              {games.map((game) => (
                <option key={game.id} value={game.id}>
                  {game.name}
                </option>
              ))}
            </Select>
          </div>
          <div className='ml-2 relative'>
            <button
              className='bg-white border border-gray-300 text-gray-900 text-sm rounded-lg p-2.5'
              onClick={() => setFilterOpen(!filterOpen)}
            >
              <Sliders size={18} />
            </button>
            {renderTypeFilter()}
          </div>
          <div className='ml-2'>
            <button
              className='bg-blue-600 text-white text-sm rounded-lg p-2.5'
              onClick={() => setShowAddModal(true)}
            >
              <Plus size={18} />
            </button>
          </div>
        </div>

        {/* Bet Limits Table */}
        {loading ? (
          <div className='text-center py-8'>
            <div className='inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-blue-600 border-r-transparent'></div>
            <p className='mt-2 text-gray-600'>Loading bet limits...</p>
          </div>
        ) : error ? (
          <div className='text-center py-8 text-red-500'>{error}</div>
        ) : (
          <>
            <div className='bg-white rounded-lg border border-gray-200 overflow-hidden'>
              <div className='grid grid-cols-5 text-sm font-medium text-gray-700 bg-gray-50 border-b border-gray-200 px-4 py-3'>
                <div>Combination</div>
                <div>Game</div>
                <div>Limit</div>
                <div>Type</div>
                <div>Actions</div>
              </div>

              <div className='divide-y divide-gray-200'>
                {filteredBetLimits.length > 0 ? (
                  filteredBetLimits.map((limit) => (
                    <div
                      key={limit.id}
                      className='grid grid-cols-5 text-sm px-4 py-3 items-center'
                    >
                      <div className='font-medium'>{limit.combination}</div>
                      <div>{getGameNameById(limit.gameId)}</div>
                      <div>{limit.limit}</div>
                      <div>
                        <Badge
                          color={limit.type === 'target' ? 'info' : 'success'}
                          className='font-normal'
                        >
                          {limit.type}
                        </Badge>
                        {limit.isSoldOut && (
                          <Badge color='danger' className='ml-2 font-normal'>
                            Sold Out
                          </Badge>
                        )}
                      </div>
                      <div className='flex gap-2'>
                        <button
                          className='text-blue-600 hover:text-blue-800'
                          onClick={() => handleEditLimit(limit)}
                        >
                          <Edit size={16} />
                        </button>
                        <button
                          className='text-red-600 hover:text-red-800'
                          onClick={() => handleDeleteLimit(limit.id)}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className='text-center py-8 text-gray-500'>
                    No bet limits found
                  </div>
                )}
              </div>
            </div>

            {/* Pagination */}
            <div className='flex justify-between items-center mt-4'>
              <button
                onClick={handlePrevPage}
                disabled={page === 1}
                className={`flex items-center space-x-1 px-3 py-2 rounded-lg ${
                  page === 1
                    ? 'text-gray-400 cursor-not-allowed'
                    : 'text-blue-600'
                }`}
              >
                <ChevronLeft size={16} />
                <span>Previous</span>
              </button>

              <span className='text-sm text-gray-700'>
                Page {page} of {totalPages}
              </span>

              <button
                onClick={handleNextPage}
                disabled={page === totalPages}
                className={`flex items-center space-x-1 px-3 py-2 rounded-lg ${
                  page === totalPages
                    ? 'text-gray-400 cursor-not-allowed'
                    : 'text-blue-600'
                }`}
              >
                <span>Next</span>
                <ChevronRight size={16} />
              </button>
            </div>
          </>
        )}
      </div>

      {/* Add Limit Modal */}
      <Modal
        show={showAddModal}
        onClose={() => setShowAddModal(false)}
        popup
        size='md'
      >
        <Modal.Header className='px-6 pt-6 pb-0 flex justify-between items-center'>
          <h3 className='text-xl font-medium text-gray-900'>
            Add New Bet Limit
          </h3>
        </Modal.Header>
        <Modal.Body className='px-6 py-4'>
          {renderLimitForm()}
          <div className='flex justify-end gap-4 mt-6'>
            <Button color='gray' onClick={() => setShowAddModal(false)}>
              Cancel
            </Button>
            <Button color='blue' onClick={handleAddLimit}>
              <Plus className='mr-2 h-5 w-5' />
              Add Limit
            </Button>
          </div>
        </Modal.Body>
      </Modal>

      {/* Edit Limit Modal */}
      <Modal
        show={showEditModal}
        onClose={() => setShowEditModal(false)}
        popup
        size='md'
      >
        <Modal.Header className='px-6 pt-6 pb-0 flex justify-between items-center'>
          <h3 className='text-xl font-medium text-gray-900'>Edit Bet Limit</h3>
          <button
            className='text-gray-500 hover:text-gray-900'
            onClick={() => setShowEditModal(false)}
          >
            <X size={20} />
          </button>
        </Modal.Header>
        <Modal.Body className='px-6 py-4'>
          {renderLimitForm()}
          <div className='flex justify-end gap-4 mt-6'>
            <Button color='gray' onClick={() => setShowEditModal(false)}>
              Cancel
            </Button>
            <Button color='blue' onClick={handleUpdateLimit}>
              <Check className='mr-2 h-5 w-5' />
              Update Limit
            </Button>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
}
